import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  Snackbar,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Paper,
  Chip,
} from '@mui/material';
import { Upload as UploadIcon, Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { useState, useRef, useEffect } from 'react';
import { updateService } from '../services/updateService';
import { useDevices } from '../hooks/useDevices';
import { auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Device } from '../types';

interface BulkUpdateModalProps {
  open: boolean;
  onClose: () => void;
  devices: Device[];
}

type UpdateType = 'update' | 'newInstall';

const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({ open, onClose, devices }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [selectedDevices, setSelectedDevices] = useState<Record<string, boolean>>({});
  const [updateType, setUpdateType] = useState<UpdateType>('update');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const { requestUpdate } = useDevices(!!user);
  
  const [completeCount, setCompleteCount] = useState(0);
  const [totalSelected, setTotalSelected] = useState(0);

  // Reset selected devices when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedDevices({});
      setSelectedFile(null);
      setError(null);
      setUploadProgress(0);
      setStatusMessage('');
      setSearchTerm('');
      setUpdateType('update');
      setUploading(false);
      setCompleteCount(0);
      setTotalSelected(0);
    }
  }, [open]);

  // Update total selected count whenever selectedDevices changes
  useEffect(() => {
    const count = Object.values(selectedDevices).filter(selected => selected).length;
    setTotalSelected(count);
  }, [selectedDevices]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.apk')) {
        // Verificar tamanho do arquivo (máximo 100MB)
        if (file.size > 100 * 1024 * 1024) {
          setError('O arquivo é muito grande. O tamanho máximo permitido é 100MB.');
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);
        setError(null);
      } else {
        setError('Por favor, selecione um arquivo APK válido');
        setSelectedFile(null);
      }
    }
  };

  const handleSelectAll = () => {
    const newSelected: Record<string, boolean> = {};
    const filteredDevices = devices.filter(device => {
      const search = searchTerm.toLowerCase();
      return (
        (device.customName?.toLowerCase().includes(search)) ||
        (device.name?.toLowerCase().includes(search)) ||
        (device.companyName?.toLowerCase().includes(search)) ||
        (device.code?.toLowerCase().includes(search)) ||
        (device.id?.toLowerCase().includes(search))
      );
    });

    const allSelected = filteredDevices.every(device => selectedDevices[device.id]);
    
    filteredDevices.forEach(device => {
      newSelected[device.id] = !allSelected;
    });

    setSelectedDevices({ ...selectedDevices, ...newSelected });
  };

  const handleToggleDevice = (deviceId: string) => {
    setSelectedDevices(prev => ({
      ...prev,
      [deviceId]: !prev[deviceId]
    }));
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    // Verificar se há dispositivos selecionados
    const selectedDevicesIds = Object.entries(selectedDevices)
      .filter(([_, selected]) => selected)
      .map(([deviceId]) => deviceId);

    if (selectedDevicesIds.length === 0) {
      setError('Selecione pelo menos um dispositivo');
      return;
    }

    // Verificar autenticação
    if (!auth.currentUser) {
      setError('Você precisa estar autenticado para fazer upload');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setStatusMessage('Preparando arquivo...');
    setCompleteCount(0);

    try {
      // Upload do APK uma única vez para obter a URL
      const upload = await updateService.uploadApk(
        'bulk', // Usamos um ID genérico para armazenar o APK
        selectedFile,
        (progress) => {
          setUploadProgress(progress);
          
          // Atualizar mensagem de status com base no progresso
          if (progress <= 10) {
            setStatusMessage('Preparando arquivo...');
          } else if (progress <= 30) {
            setStatusMessage('Lendo arquivo...');
          } else if (progress <= 50) {
            setStatusMessage('Iniciando upload...');
          } else if (progress <= 80) {
            setStatusMessage('Enviando arquivo...');
          } else if (progress <= 90) {
            setStatusMessage('Finalizando upload...');
          } else {
            setStatusMessage('Upload concluído! Atualizando dispositivos...');
          }
        }
      );
      
      // Após o upload, solicitar atualização para cada dispositivo selecionado
      setStatusMessage(`Enviando solicitações para ${selectedDevicesIds.length} dispositivos...`);
      
      // Atualizando o contador à medida que cada dispositivo é processado
      let completedCount = 0;
      
      // Processar dispositivos em paralelo (com limite)
      const batchSize = 5;
      for (let i = 0; i < selectedDevicesIds.length; i += batchSize) {
        const batch = selectedDevicesIds.slice(i, i + batchSize);
        await Promise.all(
          batch.map(async (deviceId) => {
            try {
              // Associar o APK ao dispositivo
              await updateService.assignApkToDevice(deviceId, upload.apkUrl, updateType);
              // Solicitar atualização
              await requestUpdate(deviceId);
              completedCount++;
              setCompleteCount(completedCount);
            } catch (err) {
              console.error(`Erro ao atualizar dispositivo ${deviceId}:`, err);
            }
          })
        );
      }
      
      setStatusMessage(`Atualização iniciada para ${completedCount} dispositivos!`);
      
      // Fechar o modal após um pequeno atraso para mostrar a mensagem de sucesso
      setTimeout(() => {
        onClose();
        setSelectedFile(null);
        setSelectedDevices({});
      }, 2000);
    } catch (error: any) {
      console.error('Erro no upload:', error);
      if (error.message === 'Usuário não está autenticado') {
        setError('Você precisa estar autenticado para fazer upload');
      } else if (error.code === 'storage/unauthorized') {
        setError('Você não tem permissão para fazer upload neste local');
      } else {
        setError(`Erro ao fazer upload do arquivo: ${error.message || 'Tente novamente'}`);
      }
    } finally {
      setUploading(false);
    }
  };

  // Filtra dispositivos com base no termo de pesquisa
  const filteredDevices = devices.filter(device => {
    const search = searchTerm.toLowerCase();
    return (
      (device.customName?.toLowerCase().includes(search)) ||
      (device.name?.toLowerCase().includes(search)) ||
      (device.companyName?.toLowerCase().includes(search)) ||
      (device.code?.toLowerCase().includes(search)) ||
      (device.id?.toLowerCase().includes(search))
    );
  });

  // Verifica se todos os dispositivos filtrados estão selecionados
  const allSelected = filteredDevices.length > 0 && 
    filteredDevices.every(device => selectedDevices[device.id]);

  return (
    <Dialog 
      open={open} 
      onClose={!uploading ? onClose : undefined} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Atualização em Massa de Dispositivos</Typography>
          {!uploading && (
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              1. Selecione o tipo de atualização
            </Typography>

            <FormControl component="fieldset">
              <RadioGroup
                row
                value={updateType}
                onChange={(e) => setUpdateType(e.target.value as UpdateType)}
              >
                <FormControlLabel 
                  value="update" 
                  control={<Radio />} 
                  label="Atualização" 
                  disabled={uploading}
                />
                <FormControlLabel 
                  value="newInstall" 
                  control={<Radio />} 
                  label="Nova Instalação" 
                  disabled={uploading}
                />
              </RadioGroup>
            </FormControl>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              2. Selecione o APK
            </Typography>

            <Box
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: 'grey.300',
                borderRadius: 1,
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                ...(!uploading && {
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  }
                })
              }}
              onClick={() => !uploading && fileInputRef.current?.click()}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".apk"
                style={{ display: 'none' }}
                disabled={uploading}
              />
              <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {selectedFile ? selectedFile.name : 'Clique para selecionar o APK'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Apenas arquivos .apk são aceitos (máx. 100MB)
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                3. Selecione os dispositivos
              </Typography>
              
              <Chip 
                label={`${totalSelected} selecionados`} 
                color="primary" 
                size="small"
                sx={{ ml: 1 }}
              />
            </Box>

            <TextField
              fullWidth
              placeholder="Buscar dispositivos..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={uploading}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            <Box 
              sx={{ 
                bgcolor: 'background.paper', 
                border: '1px solid', 
                borderColor: 'divider',
                borderRadius: 1,
                maxHeight: '250px',
                overflow: 'auto'
              }}
            >
              <List disablePadding dense>
                <ListItem
                  button
                  onClick={handleSelectAll}
                  disabled={uploading || filteredDevices.length === 0}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={allSelected}
                      indeterminate={totalSelected > 0 && !allSelected}
                      disabled={uploading || filteredDevices.length === 0}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight="bold">Selecionar todos</Typography>}
                  />
                </ListItem>
                
                <Divider />
                
                {filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => (
                    <ListItem
                      key={device.id}
                      button
                      onClick={() => handleToggleDevice(device.id)}
                      disabled={uploading}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox
                          edge="start"
                          checked={!!selectedDevices[device.id]}
                          disabled={uploading}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" fontWeight="500">
                              {device.customName || device.name}
                            </Typography>
                            {device.customName && (
                              <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                                {device.name}
                              </Typography>
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                            {device.companyName && (
                              <Chip
                                label={device.companyName}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ 
                                  height: 16,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  '& .MuiChip-label': { px: 0.6 }
                                }}
                              />
                            )}
                            {device.code && (
                              <Chip
                                label={device.code}
                                size="small"
                                sx={{ 
                                  height: 16,
                                  fontSize: '0.6rem',
                                  fontWeight: 600,
                                  bgcolor: 'grey.800',
                                  color: 'white',
                                  '& .MuiChip-label': { px: 0.6 }
                                }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                              {device.id}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color="text.secondary" align="center">
                          Nenhum dispositivo encontrado
                        </Typography>
                      }
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </Paper>

          {uploading && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{statusMessage}</span>
                <span>{uploadProgress.toFixed(0)}%</span>
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
              
              {completeCount > 0 && totalSelected > 0 && (
                <Typography variant="body2" color="text.secondary" align="center">
                  Processando dispositivos: {completeCount} de {totalSelected} concluídos
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} disabled={uploading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || totalSelected === 0 || uploading}
          startIcon={<UploadIcon />}
        >
          Atualizar Dispositivos
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateModal; 